/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/shared/shadcn/components/ui/card'
import Table from '@/shared/components/Table/Table'
import { TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import { useSoldItemsQuantityColumnsConfig } from '../../hooks/useSoldItemsQuantityColumnsConfig'
import Tabs from '@/shared/components/Tabs/Tabs'
import Select from '@/shared/components/Select/Select'
import { OptionType } from '@/shared/api/BaseAPI.model'
import InsightsAPI from '@/features/dashboard/api/insights/InsightsAPI'
import { QUERY } from '@/features/dashboard/api/insights/InsightsAPI.model'
import { msUntilNext6AM } from '@/shared/utils/getMsUntilNext6AM'
import { dashboardEndDate, daysBefore } from '@/features/dashboard/utils/dateRange'

interface QuantityTableProps {
  title: string
  flex?: number | 'auto'
}

export function QuantityTable(props: QuantityTableProps) {
  const { title, flex } = props
  const { t } = useTranslation()
  const columns = useSoldItemsQuantityColumnsConfig()
  const columnsModel = ['name', 'quantity', 'orderedWith', 'percentage']

  const ingredientTypes = useMemo(
    () =>
      [
        // The unit itself is not translated, only the quantity it measures.
        {
          label: `${t('SALES_REPORTS.FILTER.MEASUREMENT.WEIGHT')} (kg)`,
          value: 'weight'
        },
        {
          label: `${t('SALES_REPORTS.FILTER.MEASUREMENT.VOLUME')} (l)`,
          value: 'volume'
        },
        { label: t('SALES_REPORTS.FILTER.MEASUREMENT.COUNT'), value: 'count' }
      ] as OptionType[],
    [t]
  )

  const [selectedIngredientType, setSelectedIngredientType] =
    useState<OptionType>(ingredientTypes[0])
  const [selectedTab, setSelectedTab] = useState(0)

  const currentLanguage = localStorage.getItem('i18nextLng') || 'en'

  const flexClass = (() => {
    switch (flex) {
      case 1:
        return 'flex-1'
      case 2:
        return 'flex-2'
      case 'auto':
        return 'flex-auto'
      default:
        return 'flex-1'
    }
  })()

  const quantityTableTabs = [
    { label: t('DASHBOARD.TABS.DISHES'), value: 0 },
    { label: t('DASHBOARD.TABS.TOPPINGS'), value: 1 },
    { label: t('DASHBOARD.TABS.INGREDIENTS'), value: 2 }
  ]

  const [timeRange, setTimeRange] = useState('90d')

  const daysToSubtract = (() => {
    switch (timeRange) {
      case '7d':
        return 7
      case '30d':
        return 30
      default:
        return 90
    }
  })()

  const options = useMemo(
    () => [
      { label: t('DASHBOARD.LAST_3_MONTHS'), value: '90d' },
      { label: t('DASHBOARD.LAST_30_DAYS'), value: '30d' },
      { label: t('DASHBOARD.LAST_7_DAYS'), value: '7d' }
    ],
    [t]
  )

  const selected = useMemo(
    () => options.find(o => o.value === timeRange) ?? options[0],
    [options, timeRange]
  )

  const endDateStr = useMemo(dashboardEndDate, [])
  const startDateStr = daysBefore(endDateStr, daysToSubtract)

  const queryOptions = {
    staleTime: msUntilNext6AM(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  }

  const { data: topByUnitsToppings, isLoading: topByUnitsToppingsLoading } =
    useQuery({
      queryKey: [
        QUERY.TOP_BY_UNITS_TOPPINGS,
        startDateStr,
        endDateStr,
        'quantity-table'
      ],
      queryFn: () =>
        InsightsAPI.getTopByUnitsToppings({
          start_date: startDateStr,
          end_date: endDateStr,
          limit: 10
        }),
      enabled: selectedTab === 1,
      ...queryOptions
    })

  const { data: topByUnitsDishes, isLoading: topByUnitsDishesLoading } =
    useQuery({
      queryKey: [
        QUERY.TOP_BY_UNITS_DISHES,
        startDateStr,
        endDateStr,
        'quantity-table'
      ],
      queryFn: () =>
        InsightsAPI.getTopByUnitsDishes({
          start_date: startDateStr,
          end_date: endDateStr,
          limit: 10
        }),
      enabled: selectedTab === 0,
      ...queryOptions
    })

  const {
    data: topByUnitsIngredientsMv,
    isLoading: topByUnitsIngredientsMvLoading
  } = useQuery({
    queryKey: [
      QUERY.TOP_BY_UNITS_INGREDIENTS_MV,
      startDateStr,
      endDateStr,
      selectedIngredientType.value,
      'quantity-table'
    ],
    queryFn: () =>
      InsightsAPI.getTopByUnitsIngredientsMv({
        start_date: startDateStr,
        end_date: endDateStr,
        limit: 10,
        type_filter: selectedIngredientType.value
      }),
    enabled: selectedTab === 2,
    ...queryOptions
  })

  const isLoading =
    topByUnitsToppingsLoading ||
    topByUnitsDishesLoading ||
    topByUnitsIngredientsMvLoading

  type TopItem = {
    uuid: string
    name: string
    units: number
    percentage: number
  }

  const mapToRows = (items?: TopItem[]) =>
    (items ?? []).map(item => ({
      id: item.uuid,
      name: item.name,
      quantity: item.units,
      orderedWith:
        selectedTab === 0
          ? [
              {
                id: item.uuid,
                label: t('DASHBOARD.TOP_DISHES'),
                tooltipContent: ['fires', 'cheeseburger', 'large fries']
              }
            ]
          : undefined,
      percentage: `${item.percentage}%`
    }))

  const getTableData = () => {
    switch (selectedTab) {
      case 0:
        return mapToRows(topByUnitsDishes?.tops?.[currentLanguage] as any)
      case 1:
        return mapToRows(topByUnitsToppings?.tops?.[currentLanguage] as any)
      case 2:
        return mapToRows(
          topByUnitsIngredientsMv?.tops?.[currentLanguage] as any
        )
      default:
        return []
    }
  }

  const tableData = getTableData()

  return (
    <Card
      className={`pt-0 relative ${flexClass} gap-0 py-0 overflow-hidden`}
      style={{
        minWidth: 0,
        borderWidth: '0.125rem'
      }}
    >
      <CardHeader className="flex items-start gap-2 space-y-0 border-b py-5 sm:flex-row flex-wrap">
        <div className="flex w-full justify-between">
          <div className="grid flex-1 gap-1">
            <CardTitle>{title}</CardTitle>
          </div>
          <div
            className={`grid pr-2 gap-1 flex justify-end ${
              selectedTab === 2 ? 'visible' : 'invisible'
            }`}
          >
            <Select
              options={ingredientTypes}
              value={selectedIngredientType}
              onChange={value => setSelectedIngredientType(value as OptionType)}
              minWidth="10rem"
              maxWidth="11rem"
              hideClearIcon
            />
          </div>
          <div className="grid flex justify-end">
            <Select
              options={options}
              value={selected}
              onChange={option => setTimeRange((option as OptionType).value)}
              minWidth="9rem"
              maxWidth="11rem"
              hideClearIcon
              widthFitContent
            />
          </div>
        </div>
        <div className="flex w-full">
          <Tabs
            tabs={quantityTableTabs}
            value={selectedTab}
            onChange={(value: string | number) => {
              if (typeof value === 'number') {
                setSelectedTab(value)
              }
            }}
          />
        </div>
      </CardHeader>
      <CardContent
        className="px-1 pt-2 sm:px-6 sm:pt-6 pb-6 h-full"
        style={{
          overflow: 'hidden',
          minWidth: 0
        }}
      >
        <Table
          columns={columns}
          columnsModel={columnsModel}
          tableKey={TABLE_KEYS.QUANTITY_TABLE}
          initialRows={tableData || []}
          filterModel={{ items: [] }}
          handleOnDeleteItems={() => null}
          handleOnAddItem={() => null}
          hideToolbar
          hideCheckboxSelection
          hideFooter
          disablePagination
          columnVisibilityModel={{ orderedWith: selectedTab === 0 }}
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  )
}
